/**
 * What a production deploy will ship — the shapes and the one rule, shared by
 * the API that builds the plan and the dialog that renders it.
 *
 * Pure and dependency-free, so the browser imports it as it is.
 *
 * The rule is the general form of a "verified" gate: a deploy ships the
 * newest commit on the default branch that carries a successful verified
 * check (a commit status or check run, named in Settings) — the commits after
 * it wait for the next one. A project with no gate ships the tip, and so does
 * a deploy whose bypass input is on: that input is the incident path, where
 * the newest code is the point.
 */

/** One `workflow_dispatch` input, as the workflow file declares it. */
export interface WorkflowInput {
  name: string;
  type: 'boolean' | 'string' | 'choice' | 'number' | 'environment';
  description: string | null;
  required: boolean;
  /** As declared; GitHub fills it in when the input is left out. */
  default: string | boolean | number | null;
  /** Only for `choice`. */
  options: string[];
}

/** The value an input starts at in the dialog — what GitHub would use. */
export function inputDefault(input: WorkflowInput): string | boolean {
  if (input.type === 'boolean') return input.default === true || input.default === 'true';
  if (input.default !== null) return String(input.default);
  if (input.type === 'choice') return input.options[0] ?? '';
  return '';
}

export interface DeployTask {
  id: string;
  title: string;
  status: string;
}

/** A commit on the default branch that production does not run yet. */
export interface DeployCommit {
  sha: string;
  headline: string;
  url: string;
  /**
   * The newest pending commit carrying a successful verified check — at most
   * one is marked, since that is the ship point and older ones are not asked.
   * Always false with no gate set.
   */
  verified: boolean;
  /** Rigg tasks whose pull request landed as this commit. */
  tasks: DeployTask[];
}

export interface DeployPlan {
  repo: { id: string; owner: string; name: string; default_branch: string };
  /** Every repository in the project that has the production workflow. */
  targets: { id: string; full_name: string }[];
  workflow: { id: number; name: string; runs_url: string };
  inputs: WorkflowInput[];
  gate: {
    /** Verified check name; null means every deploy ships the tip. */
    check: string | null;
    /** Boolean input that ships the tip regardless; null if none is named. */
    bypass_input: string | null;
  };
  /** What production runs now, from the live-version URL. */
  live: { sha: string; on_branch: boolean } | null;
  /** Why `live` is null, in words; null when it is not. */
  live_error: string | null;
  /** Newest first, from the tip down to (not including) the live commit. */
  commits: DeployCommit[];
  /** Settings that name something the workflow does not have. */
  warnings: string[];
}

/**
 * Split the commits production lacks into what this deploy ships and what it
 * leaves for the next one.
 *
 * With a gate and no bypass, the ship point is the newest verified commit;
 * when none of the pending commits is verified, nothing new ships.
 */
export function splitShipped(
  commits: DeployCommit[],
  options: { gated: boolean; bypass: boolean },
): { shipped: DeployCommit[]; waiting: DeployCommit[] } {
  if (!options.gated || options.bypass) return { shipped: commits, waiting: [] };
  const at = commits.findIndex((commit) => commit.verified);
  if (at === -1) return { shipped: [], waiting: commits };
  return { shipped: commits.slice(at), waiting: commits.slice(0, at) };
}

/** A commit sha out of whatever a live-version URL answers with. */
export function shaFromVersionBody(body: string): string | null {
  return body.match(/\b[0-9a-f]{40}\b/)?.[0] ?? null;
}
